import React, { useEffect, useState } from 'react';
import { Button, TopBar, Spinner } from '../components/ui';
import { useToast } from '../context/ToastContext';
import api from '../api/client';

// Questions the customer answers for a chosen service, then submits as a query.
export default function ServiceQuestions({ category, onClose, onSubmitted }) {
  const { toast } = useToast();
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({}); // qId → value(s)
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/service-questions?category=${encodeURIComponent(category)}`)
      .then((d) => { if (alive) setQuestions(d.questions || []); })
      .catch(() => { if (alive) setQuestions([]); });
    return () => { alive = false; };
  }, [category]);

  const setAnswer = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  const toggleMulti = (id, opt) => setAnswers((a) => {
    const cur = Array.isArray(a[id]) ? a[id] : [];
    return { ...a, [id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });

  const submit = async () => {
    for (const q of questions) {
      const v = answers[q._id];
      const empty = v == null || v === '' || (Array.isArray(v) && v.length === 0);
      if (q.required && empty) return toast(`Please answer: ${q.question}`, { tone: 'error' });
    }
    setBusy(true);
    const payload = {
      category,
      answers: questions
        .map((q) => ({ question: q.question, answer: answers[q._id] ?? '' }))
        .filter((a) => a.answer !== '' && !(Array.isArray(a.answer) && a.answer.length === 0)),
    };
    try {
      await api.post('/bookings', payload);
      toast("Our team will review your request and shortlist workers. Track it under 'Bookings'.", {
        title: 'Request submitted 🎉', tone: 'success', duration: 5000,
      });
      if (onSubmitted) onSubmitted(); else onClose();
    } catch (err) {
      toast(err.message || 'Please try again.', { title: 'Could not submit', tone: 'error' });
    }
    setBusy(false);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <TopBar title={category} onClose={onClose} />

      {questions === null ? (
        <Spinner />
      ) : (
        <div style={{ padding: 18, paddingBottom: 40 }}>
          <p className="muted" style={{ marginTop: 0, marginBottom: 16, lineHeight: 1.5 }}>
            Tell us what you need for {category}. We'll shortlist the right workers for you to interview.
          </p>

          {questions.length === 0 ? (
            <p className="faint center" style={{ marginBottom: 16, fontSize: 13.5 }}>
              Submit your request and our team will reach out to understand your needs.
            </p>
          ) : questions.map((q) => (
            <div key={q._id} className="card" style={{ padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700 }}>{q.question}{q.required ? ' *' : ''}</div>
              {q.type === 'select' ? (
                <div className="chip-wrap" style={{ marginTop: 10 }}>
                  {(q.options || []).map((opt) => {
                    const selected = q.multi ? (answers[q._id] || []).includes(opt) : answers[q._id] === opt;
                    return (
                      <button
                        key={opt}
                        className={`chip ${selected ? 'chip--on' : ''}`}
                        onClick={() => (q.multi ? toggleMulti(q._id, opt) : setAnswer(q._id, opt))}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <input
                  className="input"
                  style={{ marginTop: 10 }}
                  value={answers[q._id] || ''}
                  onChange={(e) => setAnswer(q._id, e.target.value)}
                  placeholder="Type your answer"
                />
              )}
            </div>
          ))}

          <Button title={busy ? 'Submitting…' : 'Submit Request'} onClick={submit} disabled={busy} style={{ marginTop: 8 }} />
        </div>
      )}
    </div>
  );
}
